import type { SwingDirection } from '../net/colyseus-client';

/**
 * Keyboard input, and the socket a thumb plugs into.
 *
 * This file used to open "No touch/mobile support — explicitly out of scope
 * for the MVP", which was honest and is no longer true: `touch-controls.ts`
 * feeds `touchDirection` and `touchSprint` here rather than forking the
 * movement path, so everything downstream — `camera.worldDirection`, the 20Hz
 * send, the fly-through skip — reads one vector and never learns which hand
 * produced it.
 *
 * The mouse is not handled here: turning the camera, swinging (LMB), blocking
 * (RMB) and the shoulder swap (MMB) all live with the camera rig and the
 * bootstrap, because they need the Pointer Lock state this class knows nothing
 * about — see `over-shoulder-camera-controller.ts`.
 *
 *   WASD    move
 *   Shift   sprint (hold)
 *   Ctrl    crouch (hold) — camera-and-controls.md amendment (2026-08-20)
 *   Space   jump — camera-and-controls.md amendment (2026-10-16); dodge is passive now
 *   J/K/L   attack LEFT / OVERHEAD / RIGHT
 *   F       parry
 *   R       feint (cancel an in-progress attack windup) — combat-depth.md
 *   Q       class ability (Vanish / Brace / Warcry)
 *   E       interact (hold for crank-type actions) — dungeon-instance.md only
 *   T       toggle personal lantern — design/gdd/lighting.md
 */
export class InputController {
  private readonly held = new Set<string>();
  /** Space. Was `onDodge` until the active dodge was removed (2026-10-16). */
  onJump: (() => void) | null = null;
  onAttack: ((direction: SwingDirection) => void) | null = null;
  onParry: (() => void) | null = null;
  /** combat-depth.md (2026-08-21). */
  onFeint: (() => void) | null = null;
  /** design/gdd/lighting.md (2026-08-21). */
  onLanternToggle: (() => void) | null = null;
  /**
   * Emote picker toggle, on **B** — the key `05d9e7e` freed when it removed
   * bandages and deliberately left inert. Every other letter this game uses is
   * a combat or interaction verb.
   */
  onEmoteMenu: (() => void) | null = null;
  onAbility: (() => void) | null = null;
  /** MMO combat integration (2026-08-24) — 1-based action-bar slot. */
  onAbilitySlot: ((slot: number) => void) | null = null;
  /** MMO combat integration — cycle the soft target (Tab). */
  onCycleTarget: (() => void) | null = null;
  /**
   * Ally targeting (2026-10-16) — F1 targets yourself, F2–F4 the squadmates in
   * party-frame order, the MMO convention. F1 was the debug overlay's key
   * until that became a developer-build-only tool. 0-based index.
   */
  onSelectPartyMember: ((index: number) => void) | null = null;
  onSprintChange: ((active: boolean) => void) | null = null;
  onCrouchChange: ((active: boolean) => void) | null = null;
  /** Generic world-object interaction — the engine decides what "interact" means (bar a door, start a crank, open a chest, ...). */
  onInteractStart: (() => void) | null = null;
  onInteractEnd: (() => void) | null = null;
  /** The gate's other option — descend instead of banking the run. See the `KeyG` case. */
  onDescend: (() => void) | null = null;
  /** Opens and closes the carried-loot list. See the `KeyI` case. */
  onToggleInventory: (() => void) | null = null;

  private readonly keydownHandler = (e: KeyboardEvent) => this.handleKeydown(e);
  private readonly keyupHandler = (e: KeyboardEvent) => this.handleKeyup(e);

  start(): void {
    window.addEventListener('keydown', this.keydownHandler);
    window.addEventListener('keyup', this.keyupHandler);
  }

  stop(): void {
    window.removeEventListener('keydown', this.keydownHandler);
    window.removeEventListener('keyup', this.keyupHandler);
  }

  /**
   * Forgets every held key (2026-09-02).
   *
   * Opening the run menu takes focus away from the game, and a `keyup` that
   * lands while the menu is up never reaches the scene — so without this a
   * player who paused mid-sprint would resume still sprinting into a wall.
   */
  releaseAll(): void {
    this.held.clear();
    this.touchDirection = { x: 0, z: 0 };
    this.touchSprint = false;
  }

  /**
   * The analog stick, when there is one (2026-09-05).
   *
   * Written every frame by `input/touch-controls.ts` and zero the rest of the
   * time. It *overrides* the keys rather than summing with them: a device with
   * both is a tablet with a keyboard, and adding the two vectors would make a
   * held W and a stick pushed left produce a diagonal neither hand asked for.
   */
  touchDirection: { x: number; z: number } = { x: 0, z: 0 };
  /** Set by the same source when the stick is pushed past its sprint ring. */
  touchSprint = false;

  /** Normalized world-space move direction — the stick if it is pushed, otherwise WASD. */
  get direction(): { x: number; z: number } {
    if (this.touchDirection.x !== 0 || this.touchDirection.z !== 0) return this.touchDirection;
    let x = 0;
    let z = 0;
    if (this.held.has('KeyW')) z += 1;
    if (this.held.has('KeyS')) z -= 1;
    if (this.held.has('KeyA')) x -= 1;
    if (this.held.has('KeyD')) x += 1;
    return { x, z };
  }

  get sprintHeld(): boolean {
    return this.touchSprint || this.held.has('ShiftLeft') || this.held.has('ShiftRight');
  }

  private handleKeydown(e: KeyboardEvent): void {
    if (this.held.has(e.code)) return; // ignore auto-repeat
    this.held.add(e.code);

    switch (e.code) {
      case 'Space':
        this.onJump?.();
        break;
      case 'KeyJ':
        this.onAttack?.('LEFT');
        break;
      case 'KeyK':
        this.onAttack?.('OVERHEAD');
        break;
      case 'KeyL':
        this.onAttack?.('RIGHT');
        break;
      case 'KeyF':
        this.onParry?.();
        break;
      case 'KeyR':
        this.onFeint?.();
        break;
      case 'KeyQ':
        this.onAbility?.();
        break;
      // MMO combat integration (2026-08-24) — the numbered action bar. Digit
      // keys map straight to ability slots, the convention the spec's HUD
      // implies; the bar's own buttons cover touch.
      case 'Digit1': case 'Digit2': case 'Digit3': case 'Digit4': case 'Digit5':
      case 'Digit6': case 'Digit7': case 'Digit8': case 'Digit9':
        this.onAbilitySlot?.(Number(e.code.slice(5)));
        break;
      case 'Tab':
        // Cycle the soft target. Prevented from moving focus off the canvas,
        // which would otherwise swallow every subsequent key.
        e.preventDefault();
        this.onCycleTarget?.();
        break;
      case 'F1': case 'F2': case 'F3': case 'F4':
        // Prevented: F1 opens browser help, which drops pointer lock.
        if (!this.onSelectPartyMember) break;
        e.preventDefault();
        this.onSelectPartyMember(Number(e.code.slice(1)) - 1);
        break;
      case 'KeyE':
        this.onInteractStart?.();
        break;
      // The gate's second choice (2026-09-07). Its own key, and not E,
      // because E must always mean the option that keeps your loot — see
      // `handleExtract`'s note on why the reflex press has to be the safe one.
      // Deliberately not a combat key: this is pressed once a floor, at a
      // dead-end stairwell, and it stakes an entire run.
      case 'KeyG':
        this.onDescend?.();
        break;
      // The bag, on a key as well as on its own summary bar (2026-09-07).
      // Clicking the bar needs the cursor, and the cursor is captured for
      // mouse-look — so without a key the only way to open the bag mid-run is
      // to press Escape first and give up the camera.
      case 'KeyI':
        this.onToggleInventory?.();
        break;
      case 'KeyT':
        this.onLanternToggle?.();
        break;
      case 'KeyB':
        this.onEmoteMenu?.();
        break;
      case 'ShiftLeft':
      case 'ShiftRight':
        this.onSprintChange?.(true);
        break;
      case 'ControlLeft':
      case 'ControlRight':
        this.onCrouchChange?.(true);
        break;
      default:
        break;
    }
  }

  private handleKeyup(e: KeyboardEvent): void {
    this.held.delete(e.code);
    if (e.code === 'ShiftLeft' || e.code === 'ShiftRight') {
      this.onSprintChange?.(false);
    }
    if (e.code === 'ControlLeft' || e.code === 'ControlRight') {
      this.onCrouchChange?.(false);
    }
    if (e.code === 'KeyE') {
      this.onInteractEnd?.();
    }
  }
}
